// Compact spec/convention line renderers for the output-formula-picker rich
// rows. Deliberately local to the picker: mockup-parity strings (PNG ·
// 1200x1200 · 36dpi · 16-bit · sRGB / MP4H264 · 1920x1080 · 20 Mbps) diverge
// from the panel views' nicer labels ("MP4 H.264"), which live in
// output-composition/shared/spec-labels.ts. Two callers, two strings.

import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import {
  FileSpecificationStillsSettings,
  FileSpecificationVideosSettings,
} from 'src/app/models/file-specifications/file-specification-settings';
import { StillsDimensions } from 'src/app/models/file-specifications/stills-dimensions';
import { StillsFormatSettings } from 'src/app/models/file-specifications/stills-format-settings';
import { WebPCompression } from 'src/app/models/file-specifications/webp-compression';
import { OutputVideoFormat } from 'src/app/models/file-specifications/output-video-format';
import { VideoBitRate } from 'src/app/models/file-specifications/video-bit-rate';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { FilenameConventionRangeEntry } from 'src/app/models/filename-conventions/filename-convention-range-entry';
import { TokenFormat } from 'src/app/models/filename-conventions/token-format';

const STILLS_FORMAT_TOKENS: Record<StillsFormatSettings['kind'], string> = {
  jpg: 'JPG',
  png: 'PNG',
  tiff: 'TIFF',
  webp: 'WebP',
  eps: 'EPS',
};

const VIDEO_FORMAT_TOKENS: Record<OutputVideoFormat, string> = {
  Mp4H264: 'MP4H264',
  Mp4H265: 'MP4H265',
  MovH264: 'MOVH264',
  MovH265: 'MOVH265',
  WebM: 'WebM',
  Gif: 'GIF',
};

const stillsDimensionsToken = (dimensions: StillsDimensions): string => {
  switch (dimensions.mode) {
    case 'fitWidth':
      return `w${dimensions.width}`;
    case 'fitHeight':
      return `h${dimensions.height}`;
    case 'explicit':
      return `${dimensions.width}×${dimensions.height}`;
    case 'longEdgeCap':
      return `long ${dimensions.longEdge}`;
    case 'longEdgeBox':
      return `${dimensions.longEdge}×${dimensions.shortEdge}`;
  }
};

const jpgQualityToken = (quality: number): string => `Q${quality}`;

const webpQualityToken = (compression: WebPCompression): string =>
  compression.mode === 'lossless' ? 'Lossless' : `Q${compression.quality}`;

const stillsQualityToken = (format: StillsFormatSettings): string | null => {
  switch (format.kind) {
    case 'jpg':
      return jpgQualityToken(format.quality);
    case 'png':
    case 'tiff':
      return `${format.bitDepth}-bit`;
    case 'webp':
      return webpQualityToken(format.compression);
    case 'eps':
      return null;
  }
};

export const stillsSpecLine = (settings: FileSpecificationStillsSettings): string => {
  const tokens: string[] = [
    STILLS_FORMAT_TOKENS[settings.encoding.format.kind],
    stillsDimensionsToken(settings.dimensions),
    `${settings.encoding.dpi}dpi`,
  ];
  const quality = stillsQualityToken(settings.encoding.format);
  const withQuality = quality != null ? [...tokens, quality] : tokens;
  return [...withQuality, settings.encoding.format.colorSpace].join(' · ');
};

const videoBitRateToken = (bitRate: VideoBitRate): string =>
  bitRate.mode === 'auto' ? 'Auto' : `${bitRate.mbps} Mbps`;

export const videosSpecLine = (settings: FileSpecificationVideosSettings): string =>
  [
    VIDEO_FORMAT_TOKENS[settings.format],
    `${settings.width}×${settings.height}`,
    videoBitRateToken(settings.bitRate),
  ].join(' · ');

export const specLineFor = (spec: FileSpecification | undefined): string => {
  if (spec == null) return 'Unknown spec';
  const settings = spec.value.settings;
  return settings.media === 'stills' ? stillsSpecLine(settings) : videosSpecLine(settings);
};

// Preview a Range convention: renders the first entry's tokenFormats as a
// user-facing sample "{tokenA}_{tokenB}.{ext}" pattern. The .{ext} suffix is
// appended so the picker cell shows what the final file would look like.
export const conventionRangePreview = (entry: FilenameConventionRangeEntry): string => {
  const templateBody = [...entry.tokenFormats]
    .sort((left, right) => left.order - right.order)
    .map((token: TokenFormat) => token.value)
    .join('');
  return `${templateBody}.{ext}`;
};

export const conventionEvPreview = (template: readonly TokenFormat[]): string => {
  const templateBody = [...template]
    .sort((left, right) => left.order - right.order)
    .map((token) => token.value)
    .join('');
  return `${templateBody}.{ext}`;
};

export interface ConventionSummary {
  name: string;
  preview: string;
}

// Range-mode summary: single "Name — {tokens}.{ext}" pair, using the first
// entry's tokenFormats as the representative preview. EV-mode wraps have
// N conventions and are handled via evConventionSummaries below.
export const rangeConventionSummary = (
  convention: FilenameConvention | undefined,
): ConventionSummary | null => {
  if (convention == null) return null;
  const payload = convention.value.payload;
  if (payload.mode !== 'rangeBased') return null;
  const firstEntry = payload.entries[0];
  const preview =
    firstEntry != null ? conventionRangePreview(firstEntry) : '{no entries}.{ext}';
  return { name: convention.name, preview };
};

// EV-mode summary set: ordered list of {name, preview} — the picker cell
// numbers them 1. / 2. / 3. per mockup. Preview line shows the shared
// EV template.
export const evConventionSummaries = (
  orderedIds: readonly string[],
  conventionsById: ReadonlyMap<string, FilenameConvention>,
): ConventionSummary[] =>
  orderedIds.map((id) => {
    const convention = conventionsById.get(id);
    if (convention == null) return { name: 'Unknown convention', preview: '' };
    const payload = convention.value.payload;
    if (payload.mode !== 'evBased') {
      return { name: convention.name, preview: '' };
    }
    return { name: convention.name, preview: conventionEvPreview(payload.template) };
  });
